import {isSafeRelativeFilePath} from '@packages/domains/code-libraries/data-types';
import {
  agentRuleConstraints,
  agentSkillConstraints,
  systemInstructionConstraints,
} from './constants';

export const isAgentSkillPath = (path: string): boolean => {
  const segments = path.split('/');
  const [skillName, filename] = segments;
  return Boolean(
    segments.length === 2 &&
    filename === agentSkillConstraints.fileName &&
    skillName &&
    isAgentSkillName(skillName)
  );
};

export const isAgentSkillName = (name: string): boolean =>
  /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(name) &&
  name !== agentRuleConstraints.rootFolderName &&
  name !== systemInstructionConstraints.rootFolderName;

// Folder names in this system are slug-y — no dots, no slashes, non-empty.
// A `.` in a folder segment means the caller is addressing a file shape, not
// a folder. Used by every path validator that walks intermediate segments,
// so the write side rejects paths that the read side (folder listing) can't
// later echo back through the same predicates.
const isAgentInstructionFolderSegment = (segment: string): boolean =>
  segment.length > 0 && !segment.includes('.') && !segment.includes('/');

export const isAgentRulePath = (path: string): boolean => {
  if (!path.startsWith(agentRuleConstraints.pathPrefix)) return false;
  if (!path.endsWith('.md') && !path.endsWith('.mdc')) return false;

  // Every segment between `rules/` and the leaf is an intermediate folder
  // (e.g. the `pages` in `rules/pages/investors.mdc`). A dotted intermediate
  // segment like `rules/v1.0/foo.md` would otherwise be accepted at write
  // time and then break `listFolders` when `v1.0` surfaces as a folder row.
  const segments = path.split('/');
  const intermediate = segments.slice(1, -1);
  return intermediate.every(isAgentInstructionFolderSegment);
};

// A path to a markdown file inside a skill folder, either at the skill root
// (e.g. `brand/notes.md`) or nested in a subfolder (e.g.
// `brand/references/voice.md`). Per the agentskills.io spec, depth-2 root
// companions sit alongside `SKILL.md`. Distinct from `isAgentSkillPath` (the
// skill's own SKILL.md root) — `SKILL.md` is reserved for the top-level entry
// point; treating a nested `SKILL.md` as a companion file is rejected to keep
// the spec unambiguous.
//
// Extension is `.md` only. The product locked "purely text files: `.md` for
// skills, `.mdc` for rules" — accepting `.mdc` here would let a downstream
// MCP client create `brand/voice.mdc`, which Designer would then misroute
// under Rules because the FE's `getInstructionKind` classifies any `.mdc`
// path as a rule before considering the `rules/` prefix.
export const isAgentSkillSubtreeFilePath = (path: string): boolean => {
  const segments = path.split('/');
  if (segments.length < 2) return false;

  const [skillName, ...rest] = segments;
  const filename = rest[rest.length - 1];
  const intermediate = rest.slice(0, -1);

  return Boolean(
    skillName &&
    isAgentSkillName(skillName) &&
    filename &&
    filename !== agentSkillConstraints.fileName &&
    filename.endsWith('.md') &&
    intermediate.every(isAgentInstructionFolderSegment)
  );
};

export const generatedReferenceFolderName = 'references';
export const generatedReferenceFilePrefix = 'wf-';
export const generatedReferenceFileExtension = '.md';

const generatedReferenceTypeMetadata: Readonly<
  Record<string, {fileSlug: string; title: string}>
> = {
  variable: {fileSlug: 'variables', title: 'variables'},
  variable_collection: {
    fileSlug: 'variable-collections',
    title: 'variable collections',
  },
  variable_mode: {fileSlug: 'variable-modes', title: 'variable modes'},
  style: {fileSlug: 'styles', title: 'styles'},
  component: {fileSlug: 'components', title: 'components'},
  asset: {fileSlug: 'assets', title: 'assets'},
  interaction: {fileSlug: 'interactions', title: 'interactions'},
  skill: {fileSlug: 'skills', title: 'skills'},
  rule: {fileSlug: 'rules', title: 'rules'},
  cms_item: {fileSlug: 'cms-items', title: 'CMS items'},
  cms_collection: {fileSlug: 'cms-collections', title: 'CMS collections'},
  cms_field: {fileSlug: 'cms-fields', title: 'CMS fields'},
  page: {fileSlug: 'pages', title: 'pages'},
  locale: {fileSlug: 'locales', title: 'locales'},
};

export const generatedReferenceFileName = (type: string): string => {
  const slug = generatedReferenceTypeMetadata[type]?.fileSlug ?? type;
  return `${generatedReferenceFilePrefix}${slug}${generatedReferenceFileExtension}`;
};

export const generatedReferenceFilePath = (
  skillName: string,
  type: string
): string =>
  `${skillName}/${generatedReferenceFolderName}/${generatedReferenceFileName(
    type
  )}`;

export const generatedReferenceFilePathFromFolderSegments = (
  folderSegments: string[],
  type: string
): string => [...folderSegments, generatedReferenceFileName(type)].join('/');

export const isGeneratedReferenceFileName = (name: string): boolean =>
  name.startsWith(generatedReferenceFilePrefix) &&
  name.endsWith(generatedReferenceFileExtension) &&
  name.length >
    generatedReferenceFilePrefix.length +
      generatedReferenceFileExtension.length;

export const generatedReferenceFileTitle = (
  name: string
): string | undefined => {
  if (!isGeneratedReferenceFileName(name)) return undefined;

  const slug = name.slice(
    generatedReferenceFilePrefix.length,
    -generatedReferenceFileExtension.length
  );
  const title =
    Object.entries(generatedReferenceTypeMetadata).find(
      ([type, metadata]) => type === slug || metadata.fileSlug === slug
    )?.[1].title ?? slug.replace(/[-_]+/gu, ' ').trim();

  return title ? `Webflow ${title}` : undefined;
};

export const isGeneratedReferenceFilePath = (path: string): boolean => {
  const [skillName, folderName, fileName, ...rest] = path.split('/');
  return Boolean(
    rest.length === 0 &&
    skillName &&
    isAgentSkillName(skillName) &&
    folderName === generatedReferenceFolderName &&
    fileName &&
    isGeneratedReferenceFileName(fileName)
  );
};

// A path to a subfolder inside a skill (e.g. `brand/references`). The
// trailing segment must not look like a markdown file — that's the
// disambiguator vs. `isAgentSkillSubtreeFilePath`. Intermediate segments
// share the same "slug-y, no dots" constraint so the folder listing can
// echo back any ancestor without tripping the file-shape disambiguator.
export const generatedReferenceSkillNameFromPath = (
  path: string
): string | undefined => {
  if (!isGeneratedReferenceFilePath(path)) return undefined;

  const [skillName] = path.split('/');
  return skillName && isAgentSkillName(skillName) ? skillName : undefined;
};

export const isAgentSkillSubfolderPath = (path: string): boolean => {
  const segments = path.split('/');
  if (segments.length < 2) return false;

  const [skillName, ...rest] = segments;
  const leaf = rest[rest.length - 1];
  const intermediate = rest.slice(0, -1);

  return Boolean(
    skillName &&
    isAgentSkillName(skillName) &&
    leaf &&
    !leaf.endsWith('.md') &&
    !leaf.endsWith('.mdc') &&
    !leaf.includes('.') &&
    intermediate.every(isAgentInstructionFolderSegment)
  );
};

// A path to a rule subfolder (e.g. `rules/security` or `rules/security/internal`).
// The trailing segment must NOT look like a filename — disambiguator vs.
// `isAgentRulePath` (which matches `.md`/`.mdc` files) and a guard against
// other extensions like `rules/script.ts` accidentally validating as a
// renamable folder. The bare `rules` section root is excluded: it's the
// parent every rule subfolder lives under, not a renamable subfolder itself.
// Intermediate segments share the same "slug-y, no dots" constraint so that
// `rules/v1.0/internal` doesn't slip past the leaf check on the strength of
// a dotted ancestor.
export const isAgentRuleSubfolderPath = (path: string): boolean => {
  if (!path.startsWith(agentRuleConstraints.pathPrefix)) return false;

  const segments = path.split('/');
  if (segments.length < 2) return false;

  const leaf = segments[segments.length - 1];
  const intermediate = segments.slice(1, -1);

  // Folder names in this system are slug-y (no dots). Any `.` in the leaf
  // means the caller is addressing a file, not a folder.
  return Boolean(
    leaf &&
    !leaf.includes('.') &&
    intermediate.every(isAgentInstructionFolderSegment)
  );
};

// Either a skill subfolder (e.g. `brand/references`) or a rule subfolder
// (`rules/security`). Drives the `createFolder` endpoint's path refinement —
// file-shaped paths and bare section roots are rejected.
export const isAgentInstructionSubfolderPath = (path: string): boolean =>
  isAgentSkillSubfolderPath(path) || isAgentRuleSubfolderPath(path);

// Behavior-identical to the inline implementation this replaced; the rules now
// live in code-libraries so VCC source files enforce the same definition of a
// safe relative path. See `isSafeRelativeFilePath` for why it must not be
// tightened.
export const isAgentInstructionFilePath = isSafeRelativeFilePath;
